import { useEffect, useMemo, useState } from "react";

import { UsersIcon } from "@/assets/icons";
import type { StatusFilterType } from "@/components/admin/companies";
import { CompaniesList, CompaniesToolbar } from "@/components/admin/companies";
import { Pagination } from "@/components";
import { strings } from "@/constants";
import { useListCompanies } from "@/hooks";
import type { CompanyType } from "@/types";

const PAGE_SIZE = 15;

const matchesTermHandler = (company: CompanyType, term: string): boolean => {
    const {
        email,
        name,
    } = company;

    if (!term) return true;

    const haystacks = [name, email];

    return haystacks.some((value) => value.toLowerCase().includes(term));
};

const matchesStatusHandler = (company: CompanyType, status: StatusFilterType): boolean => {
    const { isEnabled } = company;

    if (status === "all") return true;

    if (status === "enabled") return isEnabled;

    return !isEnabled;
};

export const AdminCompaniesPage = () => {
    const [search, setSearch] = useState("");

    const [statusFilter, setStatusFilter] = useState<StatusFilterType>("all");

    const [currentPage, setCurrentPage] = useState(1);

    const {
        data,
        isLoading,
        onRun: fetchCompaniesHandler,
    } = useListCompanies();

    const filteredCompanies = useMemo(
        () => {
            if (!data) return [];

            const term = search.trim().toLowerCase();

            return data.filter((company) => matchesTermHandler(
                company,
                term,
            ) && matchesStatusHandler(
                company,
                statusFilter,
            ));
        },
        [
            data,
            search,
            statusFilter,
        ],
    );

    const pagedCompanies = useMemo(
        () => filteredCompanies.slice(
            (currentPage - 1) * PAGE_SIZE,
            currentPage * PAGE_SIZE,
        ),
        [filteredCompanies, currentPage],
    );

    useEffect(
        () => void fetchCompaniesHandler({}),
        [fetchCompaniesHandler],
    );

    useEffect(
        () => setCurrentPage(1),
        [search, statusFilter],
    );

    return (
        <div
            className="
                mx-auto
                w-full
                max-w-5xl
                space-y-6
            "
        >
            <div className="flex items-center gap-2 sm:gap-3">
                <UsersIcon className="h-6 w-6 shrink-0 text-primary sm:h-7 sm:w-7" />
                <h1 className="text-xl font-bold text-text-primary sm:text-2xl">{strings.admin.companiesTitle}</h1>
            </div>
            <CompaniesToolbar
                search={search}
                statusFilter={statusFilter}
                onSearchChange={setSearch}
                onStatusChange={setStatusFilter}
            />
            <CompaniesList
                companies={pagedCompanies}
                isLoading={isLoading}
            />
            <Pagination
                currentPage={currentPage}
                pageSize={PAGE_SIZE}
                totalItems={filteredCompanies.length}
                onChange={setCurrentPage}
            />
        </div>
    );
};
